import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Upload, Info, Moon, Sun, X, FileText, Loader2, GitCompare, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Columns } from "lucide-react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.mjs";
import ToolsNav from "../components/ToolsNav";
import { AdContainer, ResponsiveAd, StickyMobileAd } from "../components/AdPlacement";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjsLib.version}/legacy/build/pdf.worker.min.mjs`;

function ComparePdf() {
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [pages1, setPages1] = useState([]);
  const [pages2, setPages2] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [viewMode, setViewMode] = useState("side-by-side"); // side-by-side, overlay, slider
  const [zoom, setZoom] = useState(100);
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging1, setIsDragging1] = useState(false);
  const [isDragging2, setIsDragging2] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return JSON.parse(saved);
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });
  const fileInput1Ref = useRef(null);
  const fileInput2Ref = useRef(null);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    document.title = "Compare PDFs - Visual PDF Comparison - KayTool";
  }, []);

  const toggleDarkMode = () => setDarkMode(!darkMode);

  const loadPdf = async (pdfFile, setPages) => {
    try {
      const arrayBuffer = await pdfFile.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const pageImages = [];
      
      for (let i = 1; i <= pdf.numPages; i++) {
        const page = await pdf.getPage(i);
        const viewport = page.getViewport({ scale: 1.5 });
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        
        await page.render({ canvasContext: context, viewport }).promise;
        pageImages.push({
          pageNum: i,
          dataUrl: canvas.toDataURL(),
          width: viewport.width,
          height: viewport.height
        });
      }
      
      setPages(pageImages);
      return pageImages.length;
    } catch (error) {
      console.error("Error loading PDF:", error);
      toast.error("Failed to load PDF");
      return 0;
    }
  };

  const handleFile1Select = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile1(selectedFile);
      setIsProcessing(true);
      const pageCount = await loadPdf(selectedFile, setPages1);
      setIsProcessing(false);
      toast.success(`PDF 1 loaded: ${pageCount} pages`);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleFile2Select = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFile2(selectedFile);
      setIsProcessing(true);
      const pageCount = await loadPdf(selectedFile, setPages2);
      setIsProcessing(false);
      toast.success(`PDF 2 loaded: ${pageCount} pages`);
    } else {
      toast.error("Please select a valid PDF file");
    }
  };

  const handleDrop1 = async (e) => {
    e.preventDefault();
    setIsDragging1(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile1(droppedFile);
      setIsProcessing(true);
      const pageCount = await loadPdf(droppedFile, setPages1);
      setIsProcessing(false);
      toast.success(`PDF 1 loaded: ${pageCount} pages`);
    }
  };

  const handleDrop2 = async (e) => {
    e.preventDefault();
    setIsDragging2(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.type === 'application/pdf') {
      setFile2(droppedFile);
      setIsProcessing(true);
      const pageCount = await loadPdf(droppedFile, setPages2);
      setIsProcessing(false);
      toast.success(`PDF 2 loaded: ${pageCount} pages`);
    }
  };

  const clearFile1 = () => {
    setFile1(null);
    setPages1([]);
    setCurrentPage(0);
  };

  const clearFile2 = () => {
    setFile2(null);
    setPages2([]);
    setCurrentPage(0);
  };

  const maxPages = Math.max(pages1.length, pages2.length);
  const bothLoaded = file1 && file2 && pages1.length > 0 && pages2.length > 0;

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className={`min-h-screen transition-colors duration-300 ${darkMode ? 'dark bg-slate-900' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`}>
      <Toaster position="top-center" richColors />
      
      <header className="border-b border-slate-200 dark:border-slate-700 bg-white/80 dark:bg-slate-800/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <GitCompare className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold text-slate-800 dark:text-white">KayTool</span>
          </a>
          <div className="flex items-center gap-4">
            <ToolsNav />
            <Badge variant="outline" className="hidden sm:flex gap-1 dark:text-slate-300">
              <Info className="w-3 h-3" /> 100% Local
            </Badge>
            <Button variant="ghost" size="icon" onClick={toggleDarkMode} className="dark:text-slate-300">
              {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
          </div>
        </div>
      </header>

      <AdContainer className="mt-4 mb-0">
        <ResponsiveAd slot="above-fold-header" mobileSize="mobile-banner" desktopSize="banner" />
      </AdContainer>

      <main className="container mx-auto px-4 py-8">
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-full mb-4">
            <GitCompare className="w-5 h-5" />
            <span className="font-medium">Compare PDFs</span>
          </div>
          <h1 className="text-4xl font-bold text-slate-800 dark:text-white mb-4">
            Compare PDF Documents
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 max-w-2xl mx-auto">
            Upload two PDFs to compare them side-by-side, page by page.
          </p>
        </div>

        {/* Upload Areas */}
        {(!file1 || !file2) && (
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto mb-8">
            {/* PDF 1 Upload */}
            <Card 
              className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
                isDragging1 ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-slate-300 dark:border-slate-600'
              }`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging1(true); }}
              onDragLeave={() => setIsDragging1(false)}
              onDrop={handleDrop1}
            >
              <CardContent className="p-8 text-center">
                <input
                  ref={fileInput1Ref}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFile1Select}
                  className="hidden"
                />
                {!file1 ? (
                  <div 
                    className="cursor-pointer"
                    onClick={() => fileInput1Ref.current?.click()}
                  >
                    <Upload className="w-12 h-12 mx-auto mb-3 text-blue-500" />
                    <h3 className="text-lg font-semibold text-slate-700 dark:text-white mb-1">
                      PDF 1 (Original)
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Drop or click to upload
                    </p>
                  </div>
                ) : (
                  <div>
                    <FileText className="w-12 h-12 mx-auto mb-3 text-blue-500" />
                    <h3 className="text-lg font-semibold text-slate-700 dark:text-white mb-1">
                      {file1.name}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                      {formatSize(file1.size)} • {pages1.length} pages
                    </p>
                    <Button variant="outline" size="sm" onClick={clearFile1}>
                      <X className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* PDF 2 Upload */}
            <Card 
              className={`border-2 border-dashed transition-all duration-300 dark:bg-slate-800 ${
                isDragging2 ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-slate-300 dark:border-slate-600'
              }`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging2(true); }}
              onDragLeave={() => setIsDragging2(false)}
              onDrop={handleDrop2}
            >
              <CardContent className="p-8 text-center">
                <input
                  ref={fileInput2Ref}
                  type="file"
                  accept=".pdf,application/pdf"
                  onChange={handleFile2Select}
                  className="hidden"
                />
                {!file2 ? (
                  <div 
                    className="cursor-pointer"
                    onClick={() => fileInput2Ref.current?.click()}
                  >
                    <Upload className="w-12 h-12 mx-auto mb-3 text-indigo-500" />
                    <h3 className="text-lg font-semibold text-slate-700 dark:text-white mb-1">
                      PDF 2 (Modified)
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400">
                      Drop or click to upload
                    </p>
                  </div>
                ) : (
                  <div>
                    <FileText className="w-12 h-12 mx-auto mb-3 text-indigo-500" />
                    <h3 className="text-lg font-semibold text-slate-700 dark:text-white mb-1">
                      {file2.name}
                    </h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">
                      {formatSize(file2.size)} • {pages2.length} pages
                    </p>
                    <Button variant="outline" size="sm" onClick={clearFile2}>
                      <X className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {isProcessing && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
            <span className="ml-3 text-slate-600 dark:text-slate-300">Loading PDF...</span>
          </div>
        )}

        {/* Comparison View */}
        {bothLoaded && !isProcessing && (
          <div className="max-w-7xl mx-auto space-y-4">
            {/* Controls */}
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  {/* Page Navigation */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                      disabled={currentPage === 0}
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm font-medium dark:text-white px-3">
                      Page {currentPage + 1} of {maxPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(Math.min(maxPages - 1, currentPage + 1))}
                      disabled={currentPage >= maxPages - 1}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* View Mode */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant={viewMode === "side-by-side" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setViewMode("side-by-side")}
                      className={viewMode === "side-by-side" ? "bg-blue-600" : ""}
                    >
                      <Columns className="w-4 h-4 mr-1" /> Side by Side
                    </Button>
                    <Button
                      variant={viewMode === "slider" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setViewMode("slider")}
                      className={viewMode === "slider" ? "bg-blue-600" : ""}
                    >
                      Slider
                    </Button>
                  </div>

                  {/* Zoom */}
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setZoom(Math.max(50, zoom - 25))}
                      disabled={zoom <= 50}
                    >
                      <ZoomOut className="w-4 h-4" />
                    </Button>
                    <span className="text-sm font-medium dark:text-white w-12 text-center">
                      {zoom}%
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setZoom(Math.min(200, zoom + 25))}
                      disabled={zoom >= 200}
                    >
                      <ZoomIn className="w-4 h-4" />
                    </Button>
                  </div>

                  {/* Clear */}
                  <Button variant="outline" size="sm" onClick={() => { clearFile1(); clearFile2(); }}>
                    <X className="w-4 h-4 mr-1" /> Clear All
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Comparison Display */}
            {viewMode === "side-by-side" && (
              <div className="grid md:grid-cols-2 gap-4">
                {/* PDF 1 */}
                <Card className="dark:bg-slate-800 overflow-hidden">
                  <div className="p-2 bg-blue-500 text-white text-center text-sm font-medium">
                    {file1.name} {pages1[currentPage] ? '' : '(No page)'}
                  </div>
                  <div className="p-4 bg-slate-100 dark:bg-slate-700 overflow-auto" style={{ maxHeight: '70vh' }}>
                    {pages1[currentPage] ? (
                      <img
                        src={pages1[currentPage].dataUrl}
                        alt={`PDF 1 - Page ${currentPage + 1}`}
                        className="mx-auto"
                        style={{ width: `${zoom}%`, maxWidth: 'none' }}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-64 text-slate-400">
                        No corresponding page
                      </div>
                    )}
                  </div>
                </Card>

                {/* PDF 2 */}
                <Card className="dark:bg-slate-800 overflow-hidden">
                  <div className="p-2 bg-indigo-500 text-white text-center text-sm font-medium">
                    {file2.name} {pages2[currentPage] ? '' : '(No page)'}
                  </div>
                  <div className="p-4 bg-slate-100 dark:bg-slate-700 overflow-auto" style={{ maxHeight: '70vh' }}>
                    {pages2[currentPage] ? (
                      <img
                        src={pages2[currentPage].dataUrl}
                        alt={`PDF 2 - Page ${currentPage + 1}`}
                        className="mx-auto"
                        style={{ width: `${zoom}%`, maxWidth: 'none' }}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-64 text-slate-400">
                        No corresponding page
                      </div>
                    )}
                  </div>
                </Card>
              </div>
            )}

            {viewMode === "slider" && (
              <Card className="dark:bg-slate-800 overflow-hidden">
                <div className="relative bg-slate-100 dark:bg-slate-700" style={{ maxHeight: '70vh', overflow: 'hidden' }}>
                  {/* PDF 2 (Background) */}
                  <div className="w-full">
                    {pages2[currentPage] ? (
                      <img
                        src={pages2[currentPage].dataUrl}
                        alt={`PDF 2 - Page ${currentPage + 1}`}
                        className="mx-auto"
                        style={{ width: `${zoom}%`, maxWidth: 'none' }}
                      />
                    ) : (
                      <div className="flex items-center justify-center h-64 text-slate-400">
                        No corresponding page in PDF 2
                      </div>
                    )}
                  </div>
                  
                  {/* PDF 1 (Overlay with clip) */}
                  {pages1[currentPage] && (
                    <div 
                      className="absolute top-0 left-0 h-full overflow-hidden"
                      style={{ width: `${sliderPosition}%` }}
                    >
                      <img
                        src={pages1[currentPage].dataUrl}
                        alt={`PDF 1 - Page ${currentPage + 1}`}
                        className="mx-auto"
                        style={{ 
                          width: `${zoom}%`, 
                          maxWidth: 'none',
                          position: 'absolute',
                          left: '50%',
                          transform: 'translateX(-50%)'
                        }}
                      />
                    </div>
                  )}
                  
                  {/* Slider Line */}
                  <div 
                    className="absolute top-0 bottom-0 w-1 bg-white shadow-lg cursor-ew-resize"
                    style={{ left: `${sliderPosition}%`, transform: 'translateX(-50%)' }}
                    onMouseDown={(e) => {
                      const handleMove = (moveEvent) => {
                        const rect = e.currentTarget.parentElement.getBoundingClientRect();
                        const newPos = ((moveEvent.clientX - rect.left) / rect.width) * 100;
                        setSliderPosition(Math.max(0, Math.min(100, newPos)));
                      };
                      const handleUp = () => {
                        document.removeEventListener('mousemove', handleMove);
                        document.removeEventListener('mouseup', handleUp);
                      };
                      document.addEventListener('mousemove', handleMove);
                      document.addEventListener('mouseup', handleUp);
                    }}
                  >
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 bg-white rounded-full shadow-lg flex items-center justify-center">
                      <div className="flex gap-0.5">
                        <div className="w-0.5 h-4 bg-slate-400 rounded"></div>
                        <div className="w-0.5 h-4 bg-slate-400 rounded"></div>
                      </div>
                    </div>
                  </div>

                  {/* Labels */}
                  <div className="absolute top-2 left-2 px-2 py-1 bg-blue-500 text-white text-xs rounded">
                    PDF 1
                  </div>
                  <div className="absolute top-2 right-2 px-2 py-1 bg-indigo-500 text-white text-xs rounded">
                    PDF 2
                  </div>
                </div>
                
                {/* Slider Input */}
                <div className="p-4">
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={sliderPosition}
                    onChange={(e) => setSliderPosition(Number(e.target.value))}
                    className="w-full"
                  />
                  <p className="text-center text-sm text-slate-500 dark:text-slate-400 mt-1">
                    Drag slider to compare • Left: PDF 1 • Right: PDF 2
                  </p>
                </div>
              </Card>
            )}

            {/* Page Thumbnails */}
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4">
                <h4 className="font-medium dark:text-white mb-3">Page Navigator</h4>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {Array.from({ length: maxPages }).map((_, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPage(idx)}
                      className={`flex-shrink-0 w-16 h-20 rounded border-2 overflow-hidden transition-all ${
                        currentPage === idx 
                          ? 'border-blue-500 ring-2 ring-blue-200' 
                          : 'border-slate-200 dark:border-slate-600 hover:border-blue-300'
                      }`}
                    >
                      {pages1[idx] ? (
                        <img
                          src={pages1[idx].dataUrl}
                          alt={`Page ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : pages2[idx] ? (
                        <img
                          src={pages2[idx].dataUrl}
                          alt={`Page ${idx + 1}`}
                          className="w-full h-full object-cover"
                        />
                      ) : null}
                      <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs text-center">
                        {idx + 1}
                      </div>
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Info */}
            <Card className="dark:bg-slate-800">
              <CardContent className="p-4">
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-blue-500">PDF 1:</span>
                    <span className="ml-2 text-slate-600 dark:text-slate-300">{file1.name}</span>
                    <span className="ml-2 text-slate-400">({pages1.length} pages)</span>
                  </div>
                  <div>
                    <span className="font-medium text-indigo-500">PDF 2:</span>
                    <span className="ml-2 text-slate-600 dark:text-slate-300">{file2.name}</span>
                    <span className="ml-2 text-slate-400">({pages2.length} pages)</span>
                  </div>
                </div>
                {pages1.length !== pages2.length && (
                  <p className="mt-2 text-sm text-amber-600 dark:text-amber-400">
                    Note: PDFs have different page counts ({pages1.length} vs {pages2.length})
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        <AdContainer className="mt-8">
          <ResponsiveAd slot="after-features" mobileSize="mobile-banner" desktopSize="banner" />
        </AdContainer>
      </main>

      <footer className="border-t border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-slate-600 dark:text-slate-400">
          <p>&copy; {new Date().getFullYear()} KayTool. All processing happens locally in your browser.</p>
        </div>
      </footer>

      <StickyMobileAd slot="sticky-footer" />
    </div>
  );
}

export default ComparePdf;
